// src/components/pipelines/stageOutcome.ts
//
// One vocabulary for what reaching a stage means for a deal. The server stores
// it as two booleans (isClosedWon / isClosedLost); every pipeline screen talks
// about it as one of three outcomes so "both won and lost" cannot be expressed.

import type { PipelineStage, StageOutcome } from '../../types/api';

export const OUTCOME_LABEL: Record<StageOutcome, string> = {
  open: 'Open — deal in progress',
  won: 'Won — deal closes as won',
  lost: 'Lost — deal closes as lost',
};

export const OUTCOME_SHORT: Record<StageOutcome, string> = {
  open: 'Open',
  won: 'Won',
  lost: 'Lost',
};

export function outcomeOf(stage: Pick<PipelineStage, 'isClosedWon' | 'isClosedLost'>): StageOutcome {
  if (stage.isClosedWon) return 'won';
  if (stage.isClosedLost) return 'lost';
  return 'open';
}

export interface StageDraft {
  /** Client-only key for list rendering and drag-and-drop. */
  key: string;
  name: string;
  outcome: StageOutcome;
  probability: number | null;
}

/** The starting point a New Pipeline offers. Every row is editable. */
export function defaultStageDrafts(): StageDraft[] {
  return [
    { name: 'Lead', outcome: 'open', probability: 10 },
    { name: 'Qualified', outcome: 'open', probability: 25 },
    { name: 'Proposal', outcome: 'open', probability: 50 },
    { name: 'Negotiation', outcome: 'open', probability: 75 },
    { name: 'Closed Won', outcome: 'won', probability: 100 },
    { name: 'Closed Lost', outcome: 'lost', probability: 0 },
  ].map((s, i) => ({ ...s, key: `default-${i}` }) as StageDraft);
}

/**
 * The same invariants the server enforces, checked before submit so the form
 * can point at the problem. The server remains the authority.
 */
export function stageListProblem(stages: Array<Pick<StageDraft, 'name' | 'outcome'>>): string | null {
  if (stages.length === 0) return 'Add at least one stage.';
  if (stages.length > 30) return 'A pipeline can have at most 30 stages.';
  if (stages.some((s) => !s.name.trim())) return 'Every stage needs a name.';
  const names = stages.map((s) => s.name.trim().toLowerCase());
  const dup = names.find((n, i) => names.indexOf(n) !== i);
  if (dup) return `Two stages are both named “${stages[names.indexOf(dup)].name.trim()}”.`;
  if (stages.filter((s) => s.outcome === 'won').length > 1) return 'Only one stage can mark a deal as won.';
  if (stages.filter((s) => s.outcome === 'lost').length > 1) return 'Only one stage can mark a deal as lost.';
  if (!stages.some((s) => s.outcome === 'open')) return 'Keep at least one open stage for deals in progress.';
  return null;
}

/** Move an item from one index to another, returning a new array. */
export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/** Server error body → a sentence. Never echoes raw server/driver text for unknown codes. */
export function pipelineErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { data?: { code?: string; error?: string } })?.data;
  const status = (err as { status?: number })?.status;
  const known = [
    'INVALID_STAGES',
    'INVALID_NAME',
    'TERMINAL_CONFLICT',
    'LAST_OPEN_STAGE',
    'STAGE_HAS_DEALS',
    'INVALID_DESTINATION',
    'IN_USE_BY_AUTOMATION',
    'PIPELINE_HAS_DEALS',
    'PIPELINE_ARCHIVED',
    'PIPELINE_IS_DEFAULT',
    'REORDER_MISMATCH',
  ];
  if (data?.code && known.includes(data.code) && data.error) return data.error;
  if (status === 404) return 'That pipeline or stage no longer exists. Reload and try again.';
  if (status === 403) return 'You do not have permission to manage pipelines in this workspace.';
  return fallback;
}

/** True when the pipeline is archived (read-only). */
export function isArchived(pipeline: { archivedAt?: string | null } | null | undefined): boolean {
  return !!pipeline?.archivedAt;
}
